/**
 * @fileoverview New User Onboarding – Full-Screen Profile Setup
 * 
 * Three immersive full-screen steps for new users:
 * Step 1: Name + username
 * Step 2: Country + gender preference
 * Step 3: Age range + clothing size (optional)
 * 
 * Uses OnboardingScreen layout for visual consistency with the value showcase.
 * Uses IP geolocation to pre-fill the country when possible.
 * Automatically sets currency based on selected country.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, AtSign, Check, X, User, Globe, Ruler, Sun, Moon, Monitor } from 'lucide-react';
import { COUNTRIES } from '@/hooks/useRegionalSettings';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingDots } from './OnboardingDots';
import WantWordmark from '@/assets/wntd-wordmark.svg';

interface NewUserOnboardingModalProps {
  open: boolean;
  onComplete: (data: { displayName: string; handle: string; country: string; currency: string; gender: string; ageRange?: string; clothingSize?: string }) => Promise<void>;
}

const AGE_RANGES = ['14-17', '18-24', '25-34', '35-44', '45-54', '55+'];
const CLOTHING_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const STEPS = [
  { icon: User, headline: "Let's get you set up", subtext: 'Choose your name and username' },
  { icon: Globe, headline: 'Almost done!', subtext: 'So we can show you relevant products and local retailers' },
  { icon: Ruler, headline: 'One more thing', subtext: 'Helps us find the right styles and sizes for you' },
];

function getCurrencyForCountry(countryCode: string): string {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return country?.currency || 'USD';
}

export function NewUserOnboardingModal({ open, onComplete }: NewUserOnboardingModalProps) {
  const [step, setStep] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [hasManuallyEditedHandle, setHasManuallyEditedHandle] = useState(false);
  const [handleError, setHandleError] = useState<string | null>(null);
  const [isCheckingHandle, setIsCheckingHandle] = useState(false);
  const [isHandleAvailable, setIsHandleAvailable] = useState<boolean | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedAgeRange, setSelectedAgeRange] = useState<string>('');
  const [selectedClothingSize, setSelectedClothingSize] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(true);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (!open) return;
    const detectCountry = async () => {
      setIsGeoLoading(true);
      try {
        const response = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
        if (response.ok) {
          const data = await response.json();
          const matched = COUNTRIES.find(c => c.code === data.country_code);
          if (matched) setSelectedCountry(data.country_code);
        }
      } catch { /* user selects manually */ } finally {
        setIsGeoLoading(false);
      }
    };
    detectCountry();
  }, [open]);

  useEffect(() => {
    if (hasManuallyEditedHandle) return;
    const autoHandle = displayName.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 20);
    setHandle(autoHandle);
  }, [displayName, hasManuallyEditedHandle]);

  useEffect(() => {
    const checkHandle = async () => {
      const trimmedHandle = handle.trim().toLowerCase();
      if (!trimmedHandle) { setHandleError(null); setIsHandleAvailable(null); return; }
      if (!/^[a-z0-9_]{3,20}$/.test(trimmedHandle)) {
        if (trimmedHandle.length < 3) setHandleError('Must be at least 3 characters');
        else if (trimmedHandle.length > 20) setHandleError('Must be 20 characters or less');
        else setHandleError('Only lowercase letters, numbers, and underscores');
        setIsHandleAvailable(null);
        return;
      }
      setHandleError(null);
      setIsCheckingHandle(true);
      try {
        const { data, error } = await supabase.from('profiles').select('handle').eq('handle', trimmedHandle).maybeSingle();
        if (error) { setIsHandleAvailable(true); setHandleError(null); }
        else { setIsHandleAvailable(data === null); if (data !== null) setHandleError('This handle is already taken'); }
      } catch { setIsHandleAvailable(true); setHandleError(null); } finally { setIsCheckingHandle(false); }
    };
    const debounce = setTimeout(checkHandle, 400);
    return () => clearTimeout(debounce);
  }, [handle]);

  const handleComplete = async (skip?: boolean) => {
    if (!displayName.trim() || !handle.trim() || !selectedCountry || !selectedGender || !isHandleAvailable) return;
    setIsLoading(true);
    try {
      const currency = getCurrencyForCountry(selectedCountry);
      await onComplete({
        displayName: displayName.trim(),
        handle: handle.trim().toLowerCase(),
        country: selectedCountry,
        currency,
        gender: selectedGender,
        ...(skip ? {} : { ageRange: selectedAgeRange || undefined, clothingSize: selectedClothingSize || undefined }),
      });
    } finally { setIsLoading(false); }
  };

  const isStep1Valid = displayName.trim().length > 0 && handle.trim().length >= 3 && !handleError && isHandleAvailable !== false;
  const isStep2Valid = selectedCountry && selectedGender && !isGeoLoading;

  // COUNTRIES array is already ordered: GB, US → Europe → Rest
  const sortedCountries = COUNTRIES;

  const selectedCurrency = selectedCountry ? getCurrencyForCountry(selectedCountry) : null;
  const currentInfo = STEPS[step];
  const Icon = currentInfo.icon;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-background">
      <div className="h-[100dvh] w-full flex flex-col">
        {/* Dots at top */}
        <div className="px-6 pb-2" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
          <OnboardingDots currentStep={step} totalSteps={STEPS.length} onStepClick={setStep} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="flex-1 flex flex-col"
          >
            {/* Hero area */}
            <div className="flex-[1] flex flex-col items-center justify-center px-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4, type: 'spring' }}
                className="mb-6"
              >
                <img src={WantWordmark} alt="WNTD" className="h-7 opacity-60" />
              </motion.div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4, type: 'spring' }}
                className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5"
              >
                <Icon className="w-8 h-8 text-primary" />
              </motion.div>
              <h1 className="text-[1.75rem] font-bold text-heading tracking-tight leading-[1.2] text-pretty text-center">
                {currentInfo.headline}
              </h1>
              <p className="text-body text-base leading-relaxed max-w-xs mx-auto text-center mt-2">
                {currentInfo.subtext}
              </p>
            </div>

            {/* Form area */}
            <div className="flex-[1.2] bg-background px-8 pt-4 flex flex-col" style={{ paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' }}>
              <div className="flex-1 space-y-5">
                {step === 0 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display name</Label>
                      <Input id="displayName" placeholder="Your name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="given-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="handle">Username</Label>
                      <div className="relative">
                        <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="handle"
                          placeholder="username"
                          value={handle}
                          onChange={(e) => { setHasManuallyEditedHandle(true); setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')); }}
                          className={cn("pl-9 pr-9", handleError && "border-destructive focus-visible:ring-destructive", isHandleAvailable && "border-primary focus-visible:ring-primary")}
                          maxLength={20}
                        />
                        {handle.trim() && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {isCheckingHandle ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : isHandleAvailable ? <Check className="w-4 h-4 text-primary" /> : handleError ? <X className="w-4 h-4 text-destructive" /> : null}
                          </div>
                        )}
                      </div>
                      {handleError ? <p className="text-xs text-destructive">{handleError}</p> : <p className="text-xs text-muted-foreground">Others can find you @{handle.trim() || 'username'}</p>}
                    </div>
                    <div className="space-y-3">
                      <Label>How Do You Like It?</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setTheme('light')}
                          className={cn(
                            "flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all",
                            theme === 'light' ? "border-primary bg-primary/5" : "border-muted bg-popover hover:bg-accent"
                          )}
                        >
                          <Sun className="w-6 h-6 mb-1.5 text-primary" />
                          <span className="font-medium text-sm">Light</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setTheme('dark')}
                          className={cn(
                            "flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all",
                            theme === 'dark' ? "border-primary bg-primary/5" : "border-muted bg-popover hover:bg-accent"
                          )}
                        >
                          <Moon className="w-6 h-6 mb-1.5 text-primary" />
                          <span className="font-medium text-sm">Dark</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setTheme('system')}
                          className={cn(
                            "flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all",
                            theme === 'system' ? "border-primary bg-primary/5" : "border-muted bg-popover hover:bg-accent"
                          )}
                        >
                          <Monitor className="w-6 h-6 mb-1.5 text-primary" />
                          <span className="font-medium text-sm">System</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {step === 1 && (
                  <>
                    <div className="space-y-2">
                      <Label>I generally shop for</Label>
                      <RadioGroup value={selectedGender} onValueChange={setSelectedGender} className="grid grid-cols-2 gap-3">
                        <Label htmlFor="female" className={cn("flex flex-col items-center justify-center rounded-xl border-2 border-muted bg-popover p-4 cursor-pointer transition-all hover:bg-accent", selectedGender === 'female' && "border-primary bg-primary/5")}>
                          <RadioGroupItem value="female" id="female" className="sr-only" />
                          <span className="text-2xl mb-1 text-primary">♀</span>
                          <span className="font-medium text-sm">Women's</span>
                        </Label>
                        <Label htmlFor="male" className={cn("flex flex-col items-center justify-center rounded-xl border-2 border-muted bg-popover p-4 cursor-pointer transition-all hover:bg-accent", selectedGender === 'male' && "border-primary bg-primary/5")}>
                          <RadioGroupItem value="male" id="male" className="sr-only" />
                          <span className="text-2xl mb-1 text-primary">♂</span>
                          <span className="font-medium text-sm">Men's</span>
                        </Label>
                      </RadioGroup>
                    </div>
                    <div className="space-y-2">
                      <Label>Where are you shopping from?</Label>
                      {isGeoLoading ? (
                        <div className="flex items-center justify-center py-3 border rounded-md bg-muted/50">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          <span className="text-sm text-muted-foreground">Detecting your location...</span>
                        </div>
                      ) : (
                        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                          <SelectTrigger className="w-full"><SelectValue placeholder="Select your country" /></SelectTrigger>
                          <SelectContent position="popper" sideOffset={4}>
                            {sortedCountries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>{country.name} ({country.currency})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {selectedCurrency && <p className="text-xs text-muted-foreground">Prices will be shown in {selectedCurrency}</p>}
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="space-y-3">
                      <Label>What's your age range?</Label>
                      <div className="flex overflow-x-auto gap-2 pb-1 -mx-8 px-8 scrollbar-none">
                        {AGE_RANGES.map((range) => (
                          <button key={range} type="button" onClick={() => setSelectedAgeRange(selectedAgeRange === range ? '' : range)}
                            className={cn("px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all whitespace-nowrap shrink-0", selectedAgeRange === range ? "border-primary bg-primary/5 text-primary" : "border-muted bg-popover text-foreground hover:bg-accent")}>
                            {range}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label>What size do you usually wear?</Label>
                      <div className="flex overflow-x-auto gap-2 pb-1 -mx-8 px-8 scrollbar-none">
                        {CLOTHING_SIZES.map((size) => (
                          <button key={size} type="button" onClick={() => setSelectedClothingSize(selectedClothingSize === size ? '' : size)}
                            className={cn("px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all whitespace-nowrap shrink-0", selectedClothingSize === size ? "border-primary bg-primary/5 text-primary" : "border-muted bg-popover text-foreground hover:bg-accent")}>
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Buttons */}
              <div className="space-y-3 mt-4">
                {step === 0 && (
                  <Button onClick={() => setStep(1)} disabled={!isStep1Valid} size="lg" className="w-full text-base font-semibold h-14 rounded-2xl">
                    Continue
                  </Button>
                )}
                {step === 1 && (
                  <Button onClick={() => setStep(2)} disabled={!isStep2Valid} size="lg" className="w-full text-base font-semibold h-14 rounded-2xl">
                    Continue
                  </Button>
                )}
                {step === 2 && (
                  <>
                    <Button onClick={() => handleComplete()} disabled={isLoading} size="lg" className="w-full text-base font-semibold h-14 rounded-2xl">
                      {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Setting up...</> : "Get Started"}
                    </Button>
                    <button type="button" onClick={() => handleComplete(true)} disabled={isLoading} className="w-full text-center text-sm text-muted-foreground py-2 active:text-foreground transition-colors">
                      Skip for now
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
